<template>
  <Modal ref="modal" title="Baixa Manual do Boleto" @cancel="close">
    <form class="baixa-manual-container" @submit.prevent="handleSubmit">
      <div class="form-content">
        <div class="input-group">
          <label class="required">Motivo da Baixa Forçada</label>
          <textarea
            v-model="motivo"
            placeholder="Descreva o motivo da baixa manual do boleto..."
            rows="3"
            :disabled="loading"
            required
          ></textarea>
        </div>

        <div class="input-group">
          <label class="required">Anexar Boleto Original</label>
          <div class="file-input-container">
            <input
              type="file"
              ref="boletoFile"
              @change="handleBoletoFileChange"
              accept=".pdf,.jpg,.jpeg,.png"
              :disabled="loading"
              required
            />
            <div class="file-info" v-if="boletoFile">
              <FileOutline :size="16" />
              <span>{{ boletoFile.name }}</span>
              <button type="button" @click="removeBoletoFile" class="remove-file">
                <Close :size="14" />
              </button>
            </div>
          </div>
        </div>

        <div class="input-group">
          <label class="required">Anexar Comprovante de Pagamento</label>
          <div class="file-input-container">
            <input
              type="file"
              ref="comprovanteFile"
              @change="handleComprovanteFileChange"
              accept=".pdf,.jpg,.jpeg,.png"
              :disabled="loading"
              required
            />
            <div class="file-info" v-if="comprovanteFile">
              <FileOutline :size="16" />
              <span>{{ comprovanteFile.name }}</span>
              <button type="button" @click="removeComprovanteFile" class="remove-file">
                <Close :size="14" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Button 
        class="center-button" 
        type="submit" 
        text="Confirmar Baixa Manual" 
        color="success" 
        :disabled="loading || !isFormValid">
        <CashCheck :size="18" />
      </Button>
    </form>

    <div v-if="loading" class="loading-overlay">
      <LoadingSpinner />
    </div>
  </Modal>
</template>

<script>
import Modal from 'core/Modal'
import Button from 'core/Button'
import LoadingSpinner from 'core/LoadingSpinner'
import FileOutline from 'icons/FileOutline'
import Close from 'icons/Close'
import CashCheck from 'icons/CashCheck'

export default {
  name: 'BaixaManualModal',
  components: {
    Modal,
    Button,
    LoadingSpinner,
    FileOutline,
    Close,
    CashCheck,
  },

  data() {
    return {
      faturaId: null,
      schema: '',
      motivo: '',
      boletoFile: null,
      comprovanteFile: null,
      loading: false,
    }
  },

  computed: {
    isFormValid() {
      return this.motivo.trim() && this.boletoFile && this.comprovanteFile
    }
  },

  methods: {
    open(faturaId, schema) {
      this.faturaId = faturaId
      this.schema = schema
      this.resetForm()
      this.$refs.modal.open()
    },

    close() {
      this.$refs.modal.close()
      this.resetForm()
    },

    toast(text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },

    resetForm() {
      this.motivo = ''
      this.boletoFile = null
      this.comprovanteFile = null
      this.loading = false
      
      if (this.$refs.boletoFile) {
        this.$refs.boletoFile.value = ''
      }
      if (this.$refs.comprovanteFile) {
        this.$refs.comprovanteFile.value = ''
      }
    },

    handleBoletoFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        if (this.validateFile(file)) {
          this.boletoFile = file
        } else {
          event.target.value = ''
        }
      }
    },

    handleComprovanteFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        if (this.validateFile(file)) {
          this.comprovanteFile = file
        } else {
          event.target.value = ''
        }
      }
    },

    removeBoletoFile() {
      this.boletoFile = null
      this.$refs.boletoFile.value = ''
    },

    removeComprovanteFile() {
      this.comprovanteFile = null
      this.$refs.comprovanteFile.value = ''
    },

    validateFile(file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (!allowedTypes.includes(file.type)) {
        this.toast('Apenas arquivos PDF, JPG e PNG são permitidos.')
        return false
      }

      if (file.size > maxSize) {
        this.toast('O arquivo deve ter no máximo 10MB.')
        return false
      }

      return true
    },

    async handleSubmit() {
      if (!this.isFormValid) {
        this.toast('Preencha todos os campos obrigatórios.')
        return
      }

      this.loading = true

      try {
        const formData = new FormData()
        formData.append('schema', this.schema)
        formData.append('id', this.faturaId)
        formData.append('motivo', this.motivo)
        formData.append('boleto_original', this.boletoFile)
        formData.append('comprovante_pagamento', this.comprovanteFile)

        await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/faturamento/baixa-manual`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.$toasted.show('Baixa manual realizada com sucesso!', {
          position: 'top-right',
        })

        this.$emit('submit:success')
        this.close()
      } catch (err) {
        this.toast(`Erro ao realizar baixa manual: ${err.body && err.body.mensagem ? err.body.mensagem : 'Erro desconhecido'}`)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.baixa-manual-container {
  min-width: 40vw;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .center-button {
    justify-content: center;
  }
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #606264;

    &.required::after {
      content: ' *';
      color: #e74c3c;
    }
  }

  textarea {
    padding: 0.75rem;
    border: 1px solid #DFDCDC;
    border-radius: 4px;
    font-size: 0.875rem;
    resize: vertical;
    min-height: 80px;

    &:focus {
      outline: none;
      border-color: #1C2D5A;
    }

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
  }
}

.file-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  input[type="file"] {
    padding: 0.5rem;
    border: 1px solid #DFDCDC;
    border-radius: 4px;
    font-size: 0.875rem;

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    font-size: 0.75rem;

    span {
      flex: 1;
      color: #495057;
    }

    .remove-file {
      background: none;
      border: none;
      color: #dc3545;
      cursor: pointer;
      padding: 0.25rem;
      border-radius: 2px;

      &:hover {
        background-color: #dc3545;
        color: white;
      }
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
</style>
